import { types } from 'mobx-state-tree';

import { allPriceRanges, allProducts, expand, ProductDetails } from 'helpers/fakeData';
import { safeLocalStorage } from 'helpers/localStorage';
import { uuidv4 } from 'helpers/uuid';
import { allSortOptions } from 'hooks/useMarketPlaceSortOptions';

export interface CartItem {
  id: string;
  quantity: number;
}

type CartItemWithoutQuantity = Omit<CartItem, 'quantity'>;
export function getCartItemKey(item: CartItemWithoutQuantity) {
  return item.id;
}

export function getCartTotal(cart: CartItem[]) {
  return cart.reduce((acc, item) => {
    const product = allProducts.find((p) => p.id === item.id);
    if (product == null) {
      return acc;
    }
    return acc + item.quantity * product.price;
  }, 0);
}

function saveCartToLocalStorage(cartId: string, cart: CartItem[]) {
  safeLocalStorage.setItem('cartId', cartId);
  safeLocalStorage.setItem(cartId, JSON.stringify(cart));
}

const CartItem = types.model('CartItem', {
  id: types.string,
  quantity: types.number,
});

export const MarketPlaceStore = types
  .model('MarketPlaceStore', {
    isMarketplaceFiltersOpen: types.optional(types.boolean, true),
    sortBy: types.optional(types.string, 'none'),
    selectedProductId: types.maybe(types.string),
    cart: types.optional(types.array(CartItem), []),
    cartId: types.maybe(types.string),
    selectedFilterAttributeIds: types.optional(types.array(types.string), []),
    selectedPriceRangeIds: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    toggleMarketplaceFilters() {
      self.isMarketplaceFiltersOpen = !self.isMarketplaceFiltersOpen;
    },
    setSortBy(sortBy: string) {
      self.sortBy = sortBy;
    },
    setSelectedProductId(productId: string) {
      self.selectedProductId = productId;
    },
    setCartId(cartId: string) {
      self.cartId = cartId;
    },
    addFilterAttributeId(attributeId: string) {
      if (self.selectedFilterAttributeIds.includes(attributeId)) {
        return;
      }
      self.selectedFilterAttributeIds.push(attributeId);
    },
    removeFilterAttributeId(attributeId: string) {
      self.selectedFilterAttributeIds.remove(attributeId);
    },
    setSelectedFilterAttributeIds(attributeIds: string[]) {
      self.selectedFilterAttributeIds.replace(attributeIds);
    },
    addPriceRangeId(priceRangeId: string) {
      if (self.selectedPriceRangeIds.includes(priceRangeId)) {
        return;
      }
      self.selectedPriceRangeIds.push(priceRangeId);
    },
    removePriceRangeId(priceRangeId: string) {
      self.selectedPriceRangeIds.remove(priceRangeId);
    },
    setSelectedPriceRangeIds(priceRangeIds: string[]) {
      self.selectedPriceRangeIds.replace(priceRangeIds);
    },
    addToCart(item: CartItem) {
      if (item.quantity === 0) {
        return;
      }
      const key = getCartItemKey(item);
      const existingItem = self.cart.find((i) => getCartItemKey(i) === key);
      if (existingItem) {
        existingItem.quantity = item.quantity;
      } else {
        self.cart.push(item);
      }
      if (self.cartId == null) {
        self.cartId = uuidv4();
      }
      saveCartToLocalStorage(self.cartId, self.cart);
    },
    removeFromCart(item: CartItem) {
      const key = getCartItemKey(item);
      self.cart.replace(self.cart.filter((i) => getCartItemKey(i) !== key));
      if (self.cartId == null) {
        self.cartId = uuidv4();
      }
      saveCartToLocalStorage(self.cartId, self.cart);
    },
    clearCart(cartId?: string) {
      self.cart.clear();
      const id = cartId ?? self.cartId;
      if (id != null) {
        safeLocalStorage.removeItem('cartId');
        safeLocalStorage.removeItem(id);
      }
    },
  }))
  .views((self) => ({
    shouldDisplayFiltersPanel(productId?: string) {
      return self.isMarketplaceFiltersOpen && productId == null;
    },
    get cartTotal() {
      return getCartTotal(self.cart);
    },
    filterProducts(products: ProductDetails[]) {
      const filteredProductsByAttributeIds = filterProductsByAttributeIds(
        products,
        self.selectedFilterAttributeIds,
      );
      const filteredProductsByPriceRangeIds = filterProductsByPriceRangeIds(
        filteredProductsByAttributeIds,
        self.selectedPriceRangeIds,
      );
      return sortProducts(filteredProductsByPriceRangeIds, self.sortBy);
    },
    get cartSize() {
      return self.cart.reduce((acc, item) => acc + item.quantity, 0);
    },
  }));

export const createMarketPlaceStore = () => {
  return MarketPlaceStore.create({});
};
export type TMarketPlaceStore = ReturnType<typeof createMarketPlaceStore>;

export const isPriceDimension = (dimensionId: string) => dimensionId === 'price';
export interface SortOption {
  id: string;
  label: string;
  dimensionId: string;
  direction: 'asc' | 'desc';
}
export const isNoneOption = (id?: string) => id == null || id === 'none';

function filterProductsByAttributeIds(
  products: ProductDetails[],
  filterAttributeIds: string[],
): ProductDetails[] {
  let filters = filterAttributeIds;
  if (filters.length === 0) {
    filters = ['white'];
  }
  const productsToConsider = expand(products);
  return productsToConsider.filter((product) =>
    (product.attributeIds ?? []).some((attributeId) => filters.includes(attributeId)),
  );
}

function filterProductsByPriceRangeIds(products: ProductDetails[], filterPriceRangeIds: string[]) {
  if (filterPriceRangeIds.length === 0) {
    return products;
  }
  return products.filter((product) =>
    filterPriceRangeIds.some((selectedPriceRangeId) => {
      const selectedRange = allPriceRanges.find((range) => range.id === selectedPriceRangeId);
      if (selectedRange == null) {
        return false;
      }
      if (selectedRange.max != null && selectedRange.min != null) {
        return product.price >= selectedRange.min && product.price <= selectedRange.max;
      }
      if (selectedRange.max != null) {
        return product.price <= selectedRange.max;
      }
      if (selectedRange.min != null) {
        return product.price >= selectedRange.min;
      }
      return false;
    }),
  );
}

function sortProducts(products: ProductDetails[], sortBy: string) {
  if (isNoneOption(sortBy)) {
    return products;
  }
  return products.toSorted((a, b) => {
    const sortByOption = allSortOptions.find((option) => option.id === sortBy);
    if (sortByOption == null || isNoneOption(sortByOption.id)) {
      return 0;
    }

    if (isPriceDimension(sortByOption.dimensionId)) {
      if (sortByOption.direction === 'asc') {
        return a.price - b.price;
      }
      return b.price - a.price;
    }

    return 0;
  });
}
